import {
  Building2,
  ChevronRight,
  Coins,
  FileSignature,
  Hash,
  Import,
  KeyRound,
  Landmark,
  LayoutTemplate,
  type LucideIcon,
  MailCheck,
  MousePointerClick,
  ShoppingBag,
  SlidersHorizontal,
  TextQuote,
  Users,
} from 'lucide-react'
import { Link, Navigate } from 'react-router'
import useSWR from 'swr'

import { PageHeader } from '@/components/ds'
import { ServiceMark } from '@/components/ds'
import { Loading } from '@/components/Loading'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { useFeatureCheck } from '@/hooks/useFeatureCheck'
import { Collection } from '@/interfaces/Collection'
import { Settings } from '@/interfaces/Settings'
import { useAuth } from '@/lib/auth'
import { Feature } from '@/lib/features/featureList'
import { hasRole } from '@/lib/hasRole'
import { Role,User } from '@/types/User'

interface SettingsItem {
  href: string
  icon: LucideIcon
  /** značka služby místo ikony (ServiceMark) */
  mark?: string
  title: string
  description: string
  meta?: string | null
}

interface SettingsGroup {
  title: string
  items: SettingsItem[]
}

const plural = (n: number, one: string, few: string, many: string) => (n === 1 ? one : n >= 2 && n <= 4 ? few : many)

export default function SettingsPage() {
  const { user, isLoading } = useAuth({
    middleware: 'auth',
    redirectTo: '/auth/login',
    tmp: 'Settings_getServerSideProps',
  })

  const allowed = !!user && hasRole(user.roles, [Role.owner, Role.manager])

  const { isEnabled: isEshopEnabled } = useFeatureCheck(Feature.Eshop)
  const { data: settings } = useSWR<Settings>(allowed ? '/api/v2/settings' : null, apiFetcher)
  const { data: bankAccounts } = useSWR<{ data?: unknown[] }>(allowed ? '/api/v2/bank-accounts' : null, apiFetcher)
  const { data: users } = useSWR<Collection<User>>(allowed ? '/api/v2/users' : null, apiFetcher)

  useSetPageHeader({
    breadcrumbs: [{ href: '/settings', children: 'Nastavení' }],
    newButton: 'invoice',
  })

  if (isLoading) {
    return <Loading />
  }

  if (!allowed) {
    return <Navigate to="/" replace />
  }

  const bankCount = bankAccounts?.data?.length
  const userCount = users?.data?.length
  const currencies = settings
    ? [settings.primaryCurrency, ...(settings.alternativeCurrencies ?? [])].filter(Boolean).join(', ')
    : null

  const groups: SettingsGroup[] = [
    {
      title: 'Firma a fakturace',
      items: [
        {
          href: '/settings/invoicing',
          icon: LayoutTemplate,
          title: 'Vzhled faktury',
          description: 'Náhled dokladu, ve kterém upravíte všechno na jednom místě.',
        },
        {
          href: '/settings/supplier',
          icon: Building2,
          title: 'Fakturační údaje',
          description: 'Název firmy, adresa, IČO a DIČ, které se tisknou na doklady.',
          meta: settings?.participant?.name ?? 'Nevyplněno',
        },
        {
          href: '/settings/footnote',
          icon: TextQuote,
          title: 'Text v patičce',
          description: 'Doplňující text na konci každého dokladu.',
          meta: settings ? (settings.footnote ? 'Vyplněno' : 'Nevyplněno') : null,
        },
        {
          href: '/settings/signature',
          icon: FileSignature,
          title: 'Razítko nebo podpis',
          description: 'Obrázek, který se vloží pod doklad.',
          meta: settings ? (settings.signature ? 'Nahráno' : 'Nenahráno') : null,
        },
      ],
    },
    {
      title: 'Peníze',
      items: [
        {
          href: '/settings/bank-accounts',
          icon: Landmark,
          title: 'Bankovní účty',
          description: 'Účty, na které vám odběratelé platí.',
          meta: bankCount !== undefined ? `${bankCount} ${plural(bankCount, 'účet', 'účty', 'účtů')}` : null,
        },
        {
          href: '/settings/currencies',
          icon: Coins,
          title: 'Měny',
          description: 'Hlavní měna účtu a další měny, ve kterých fakturujete.',
          meta: currencies,
        },
        {
          href: '/settings/bank-notifications',
          icon: MailCheck,
          title: 'Párování plateb z e-mailu',
          description: 'Přeposílejte upozornění z banky a platby se spárují samy.',
        },
      ],
    },
    {
      title: 'Doklady',
      items: [
        {
          href: '/settings/document-sequences',
          icon: Hash,
          title: 'Číselné řady',
          description: 'Formát čísel faktur, výzev k platbě a nákladů.',
        },
      ],
    },
    {
      title: 'Tým a přístupy',
      items: [
        {
          href: '/settings/users',
          icon: Users,
          title: 'Uživatelé',
          description: 'Kdo má do účtu přístup a s jakou rolí.',
          meta: userCount !== undefined ? `${userCount} ${plural(userCount, 'uživatel', 'uživatelé', 'uživatelů')}` : null,
        },
        {
          href: '/settings/api',
          icon: KeyRound,
          title: 'API a webhooky',
          description: 'Tokeny pro integrace a adresa pro oznámení o událostech.',
        },
      ],
    },
    {
      title: 'Nastavení účtu',
      items: [
        {
          href: '/settings/account',
          icon: SlidersHorizontal,
          title: 'Účet a moduly',
          description: 'Název účtu a zapnuté moduly, třeba pokladna nebo sklad.',
          meta: settings?.account?.name ?? null,
        },
        {
          href: '/settings/navigation',
          icon: MousePointerClick,
          title: 'Navigace v aplikaci',
          description: 'Co se stane po kliknutí na doklad v seznamu.',
          meta: settings ? (settings.documentRowClick === 'detail' ? 'Detail dokladu' : 'Boční panel') : null,
        },
      ],
    },
    ...(isEshopEnabled
      ? [
          {
            title: 'E-shop',
            items: [
              {
                href: '/settings/eshop',
                icon: ShoppingBag,
                title: 'Nastavení e-shopu',
                description: 'Ceny v košíku a požadavky na doručovací adresu.',
              },
            ],
          },
        ]
      : []),
    {
      title: 'Import',
      items: [
        {
          href: '/settings/fakturoid-import',
          icon: Import,
          mark: 'fakturoid',
          title: 'Import z Fakturoidu',
          description: 'Převezměte kontakty, faktury a náklady z Fakturoidu.',
        },
      ],
    },
  ]

  return (
    <>
      <title>Nastavení</title>
      <div className="mx-auto w-full max-w-3xl">
        <PageHeader title="Nastavení" description="Firma, doklady, peníze a přístupy k účtu." />

        <div className="space-y-8">
          {groups.map((group) => (
            <section key={group.title}>
              <h2 className="mb-2 text-[11px] font-medium tracking-[0.04em] text-muted-foreground uppercase">{group.title}</h2>
              <ul className="divide-y overflow-hidden rounded-lg border">
                {group.items.map((item) => (
                  <li key={item.href}>
                    <Link
                      to={item.href}
                      className="group flex items-center gap-4 px-4 py-3.5 transition-colors hover:bg-muted"
                    >
                      {item.mark ? (
                        <ServiceMark service={item.mark} />
                      ) : (
                        <span className="grid size-8 shrink-0 place-items-center rounded-lg bg-muted text-muted-foreground ring-1 ring-inset ring-black/5">
                          <item.icon className="size-4" strokeWidth={1.75} />
                        </span>
                      )}
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">{item.title}</span>
                        <span className="block truncate text-sm text-muted-foreground">{item.description}</span>
                      </span>
                      {item.meta && (
                        <span className="hidden max-w-48 truncate text-sm text-muted-foreground tabular sm:block">{item.meta}</span>
                      )}
                      <ChevronRight className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </div>
    </>
  )
}
